import { FILTER_KEYS } from '../constants/uiKeys';

/**
 * crop_season is keyed inconsistently across the app:
 *  - UI layer uses the camelCase key ("cropSeason" = FILTER_KEYS.CROP_SEASON)
 *  - saved / wire data uses the snake_case key ("crop_season")
 * Filters may also be an array (saved workspace, via Object.values) or an object
 * keyed by filterKey, and individual items may carry `values` or `Values`.
 *
 * These helpers read the season robustly across all of those shapes so the
 * season is never silently dropped (which for LATAM breaks CORNWINTER/CORNSUMMER).
 */

/** True when a filter item's key is the crop_season key in either UI or wire form. */
export const isCropSeasonKey = (key: unknown): boolean =>
  key === FILTER_KEYS.CROP_SEASON || key === 'crop_season';

// Treat null/undefined/empty-string as "no value" so an empty crop_season entry
// (e.g. a leftover { values: null }) doesn't shadow a sibling that holds a season.
const readItemValue = (item: any): string | undefined => {
  const v = item?.values ?? item?.Values;
  return v == null || v === '' ? undefined : v;
};

/**
 * Read the crop_season value from a filters collection (array or object,
 * UI or wire key, `values` or `Values`). Returns undefined when absent.
 *
 * When multiple crop_season entries exist (legacy data carrying both key forms),
 * the first one that actually holds a value wins — a null/empty entry never
 * shadows a valued one, regardless of order.
 */
export const getCropSeasonValue = (filters: any): string | undefined => {
  if (!filters) return undefined;
  if (Array.isArray(filters)) {
    for (const f of filters) {
      if (isCropSeasonKey(f?.key ?? f?.Key)) {
        const v = readItemValue(f);
        if (v !== undefined) return v;
      }
    }
    return undefined;
  }
  return readItemValue(filters[FILTER_KEYS.CROP_SEASON]) ?? readItemValue(filters['crop_season']);
};

/**
 * Remove every crop_season entry (either key form) from an object-shaped filters
 * map. Use before adding a canonical crop_season so a UI-keyed ("cropSeason") and
 * a wire-keyed ("crop_season") entry can't coexist and render as duplicates.
 * Arrays and nullish inputs are returned unchanged.
 */
export const stripCropSeason = (filters: any): any => {
  if (!filters || Array.isArray(filters)) return filters;
  const rest: Record<string, any> = {};
  for (const key of Object.keys(filters)) {
    if (!isCropSeasonKey(key)) rest[key] = filters[key];
  }
  return rest;
};

/**
 * Decode-at-boundary: collapse any crop_season entry(ies) in a filters collection
 * onto the single canonical UI key (FILTER_KEYS.CROP_SEASON = "cropSeason"),
 * preserving the item's other fields and the best (valued) season. Use when a
 * workspace/API response is loaded so the UI layer only ever sees the UI key —
 * the wire key ("crop_season") never leaks in, so no duplicates and no UI changes.
 * Returns the same array/object shape; collections without crop_season are unchanged.
 */
export const canonicalizeCropSeason = (filters: any): any => {
  if (!filters) return filters;

  if (Array.isArray(filters)) {
    if (!filters.some((f: any) => isCropSeasonKey(f?.key ?? f?.Key))) return filters;
    const template = filters.find((f: any) => isCropSeasonKey(f?.key ?? f?.Key)) ?? {};
    const rest = filters.filter((f: any) => !isCropSeasonKey(f?.key ?? f?.Key));
    return [
      ...rest,
      { ...template, key: FILTER_KEYS.CROP_SEASON, values: getCropSeasonValue(filters) ?? null }
    ];
  }

  if (!Object.keys(filters).some((k) => isCropSeasonKey(k))) return filters;
  const result: Record<string, any> = {};
  let template: any = {};
  for (const k of Object.keys(filters)) {
    if (isCropSeasonKey(k)) template = filters[k];
    else result[k] = filters[k];
  }
  result[FILTER_KEYS.CROP_SEASON] = {
    ...template,
    key: FILTER_KEYS.CROP_SEASON,
    values: getCropSeasonValue(filters) ?? null
  };
  return result;
};
